import {
  faAngleDown,
  faCalendarWeek,
  faChevronDoubleLeft,
  faChevronDoubleRight,
  faChevronLeft,
  faChevronRight
} from '@fortawesome/pro-light-svg-icons';
import { addDays, addMonths, addWeeks, isToday } from 'date-fns';
import { useDispatch, useSelector } from 'hooks';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ORANGE, PRIMARY } from 'constants/colors';

import {
  formatByLocale,
  getMonthInterval,
  getWeekInterval,
  getWorkweekInterval
} from 'utils/date';

import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Tooltip
} from '@mui/material';

import DatePicker from 'components/shared/DatePicker/DatePicker';
import FontAwesomeSvgIcon from 'components/shared/FontAwesomeSvgIcon';
import { useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';
import { dateNavigationPeriodAtom } from './atoms';
import { setUserPreference } from 'redux/actionCreators';

const Container = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  .date-picker {
    border: none;
  }

  button {
    margin: 0 5px;
  }
`;

export type Period = 'day' | 'week' | 'workweek' | 'month';

function capitalize(s) {
  if (!s || s.length === 1) return (s || '').toUpperCase();
  return s[0].toUpperCase() + s.slice(1);
}

interface DateNavigationProps {
  defaultPeriod: Period;
  selectedDate: Date;
  setSelectedDate: (selectedDate: Date) => void;
  onChange?: () => void;
  disabled?: boolean;
  allowMenuOptions?: boolean;
  options?: Period[];
}

export default function DateNavigation({
  defaultPeriod,
  selectedDate,
  setSelectedDate,
  disabled,
  allowMenuOptions,
  options
}: DateNavigationProps) {
  const { t } = useTranslation('common');

  const [period, setDateNavigationPeriod] = useRecoilState<Period | null>(
    dateNavigationPeriodAtom
  );

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const dispatch = useDispatch();
  const { holidays, defaultDateNavigationPeriod } = useSelector((state) => ({
    holidays: state.holidays,
    defaultDateNavigationPeriod: state.preferences.defaultDateNavigationPeriod
  }));

  const isDisabled = disabled || !period;

  let showIsHoliday = false;
  let showIsToday = false;
  let dayDescription = '';

  if (period == 'day') {
    const holiday = holidays[formatByLocale(selectedDate, 'yyyy-MM-dd')];
    showIsHoliday = Boolean(holiday);
    showIsToday = isToday(selectedDate);
    dayDescription = showIsToday ? t('today') : holiday?.description || '';
  }

  function getFormatByPeriod(period: Period | null) {
    if (!period) return;

    switch (period) {
      case 'day':
        return formatByLocale(selectedDate, 'd MMMM');
      case 'week': {
        const { startDate, endDate } = getWeekInterval(selectedDate);
        return `${formatByLocale(startDate, 'd')} - ${formatByLocale(
          endDate,
          'd MMMM'
        )}`;
      }
      case 'workweek': {
        const { startDate, endDate } = getWorkweekInterval(selectedDate);
        return `${formatByLocale(startDate, 'd')} - ${formatByLocale(
          endDate,
          'd MMMM'
        )}`;
      }
      case 'month': {
        const { startDate, endDate } = getMonthInterval(selectedDate);
        return `${formatByLocale(startDate, 'd')} - ${formatByLocale(
          endDate,
          'd MMMM'
        )}`;
      }
    }
  }

  function previous(period: Period): void {
    switch (period) {
      case 'day':
        setSelectedDate(addDays(selectedDate, -1));
        break;
      case 'week':
      case 'workweek':
        setSelectedDate(addWeeks(selectedDate, -1));
        break;
      case 'month':
        setSelectedDate(addMonths(selectedDate, -1));
        break;
    }
  }

  function next(period: Period): void {
    switch (period) {
      case 'day':
        setSelectedDate(addDays(selectedDate, 1));
        break;
      case 'week':
      case 'workweek':
        setSelectedDate(addWeeks(selectedDate, 1));
        break;
      case 'month':
        setSelectedDate(addMonths(selectedDate, 1));
        break;
    }
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const savePreference = (period: Period) => {
    dispatch(
      setUserPreference({
        key: 'defaultDateNavigationPeriod',
        value: period
      })
    );
  };

  // Set initial period if passed
  useEffect(() => {
    if (!allowMenuOptions) {
      return setDateNavigationPeriod(defaultPeriod);
    }

    if (defaultDateNavigationPeriod || defaultPeriod) {
      setDateNavigationPeriod(
        options?.includes(defaultDateNavigationPeriod)
          ? defaultDateNavigationPeriod
          : defaultPeriod
      );
    }
  }, [defaultDateNavigationPeriod, defaultPeriod]);

  return (
    <Container>
      {period === 'day' && (
        <Tooltip title={t('previousWeek') || ''} arrow>
          <IconButton
            color="primary"
            className="prev-btn"
            size="small"
            onClick={() => previous('week')}
            disabled={isDisabled}
          >
            <FontAwesomeSvgIcon icon={faChevronDoubleLeft} fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title={t('previous' + capitalize(period)) || ''} arrow>
        <IconButton
          color="primary"
          className="prev-btn"
          size="small"
          onClick={() => previous(period as Period)}
          disabled={isDisabled}
        >
          <FontAwesomeSvgIcon icon={faChevronLeft} fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={dayDescription} arrow>
        <Button
          variant="contained"
          color="lightGray"
          sx={{
            border: `1px solid #EFEFEF`,
            textTransform: 'none',
            lineHeight: 1.2,
            height: '40px',
            fontFamily: 'signikaregular',
            borderLeft:
              showIsToday || showIsHoliday
                ? `4px solid ${showIsToday ? PRIMARY : ORANGE}`
                : 'auto'
          }}
          startIcon={<FontAwesomeSvgIcon icon={faCalendarWeek} />}
          {...(allowMenuOptions && {
            endIcon: <FontAwesomeSvgIcon icon={faAngleDown} />
          })}
          disabled={isDisabled}
          onClick={handleClick}
        >
          <Box
            display="flex"
            flexDirection="column"
            textAlign="left"
            pl={1}
            pr={2}
          >
            <Box sx={{ fontFamily: 'signikaLight', fontSize: 12 }}>
              {period === 'day'
                ? capitalize(formatByLocale(selectedDate, 'EEEE'))
                : capitalize(t(`${period}`))}
            </Box>
            <Box fontSize="14px">{capitalize(getFormatByPeriod(period))}</Box>
          </Box>
        </Button>
      </Tooltip>

      {allowMenuOptions && (
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
        >
          {(options || []).map((p: Period, i: number) => (
            <MenuItem
              key={i}
              color="neutral"
              onClick={() => {
                setAnchorEl(null);
                setDateNavigationPeriod(p);
                savePreference(p);
              }}
            >
              {capitalize(t(`${p}`))}
            </MenuItem>
          ))}
        </Menu>
      )}

      <Tooltip
        title={t('next' + capitalize(period)) || ''}
        arrow
        enterDelay={300}
        enterNextDelay={300}
      >
        <IconButton
          color="primary"
          className="next-btn"
          size="small"
          onClick={() => next(period as Period)}
          disabled={isDisabled}
        >
          <FontAwesomeSvgIcon icon={faChevronRight} fontSize="small" />
        </IconButton>
      </Tooltip>
      {period === 'day' && (
        <Tooltip title={t('nextWeek') || ''} arrow>
          <IconButton
            color="primary"
            className="next-btn"
            size="small"
            onClick={() => next('week')}
            disabled={isDisabled}
          >
            <FontAwesomeSvgIcon icon={faChevronDoubleRight} fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <div className="date-picker">
        <DatePicker
          onChange={(date) => {
            if (date != null) {
              setSelectedDate(date);
            }
          }}
          date={selectedDate}
          disabled={isDisabled}
        />
      </div>
    </Container>
  );
}
